import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Card from "components/Card";
import InputField from "components/InputField";
import TextField from "components/TextField";
import Button from "components/Button";
import Slider from "components/Slider";
import { createTemplateAPI, createTemplateQuestionAPI } from "api/apiHelper";
import { toast } from "react-toastify";
import { CreateQuestionData } from "types/template-builder-types";
import { MdDelete, MdAdd } from "react-icons/md";

interface QuestionFormData {
  question_text: string;
  weightage: number;
  options: {
    "1": string;
    "2": string;
    "3": string;
    "4": string;
    "5": string;
  };
}

// 1. Define the default values here
const DEFAULT_OPTIONS = {
  "1": "Poor",
  "2": "Fair",
  "3": "Good",
  "4": "Very Good",
  "5": "Excellent",
};

// Helper for labels to clearly show score association
const SCORE_LABELS = {
  "1": "Score 1 (Lowest Performance)",
  "2": "Score 2",
  "3": "Score 3",
  "4": "Score 4",
  "5": "Score 5 (Highest Performance)",
};

const CreateTemplate = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    is_active: true,
  });

  // 2. Initialize state with DEFAULT_OPTIONS
  const [questions, setQuestions] = useState<QuestionFormData[]>([
    {
      question_text: "",
      weightage: 0,
      options: { ...DEFAULT_OPTIONS }, 
    },
  ]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData({
      ...formData,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    });
  };

  const handleQuestionChange = (
    index: number,
    field: keyof QuestionFormData,
    value: any
  ) => {
    const updatedQuestions = [...questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      [field]: value,
    };
    setQuestions(updatedQuestions);
  };

  const handleOptionChange = (
    questionIndex: number,
    optionKey: "1" | "2" | "3" | "4" | "5",
    value: string
  ) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex] = {
      ...updatedQuestions[questionIndex],
      options: {
        ...updatedQuestions[questionIndex].options,
        [optionKey]: value,
      },
    };
    setQuestions(updatedQuestions);
  };

  // 3. Update addQuestion to use DEFAULT_OPTIONS
  const addQuestion = () => {
    setQuestions([
      ...questions,
      {
        question_text: "",
        weightage: 0,
        options: { ...DEFAULT_OPTIONS },
      },
    ]);
  };

  const removeQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const calculateTotalWeightage = () => {
    return questions.reduce((sum, q) => sum + (q.weightage || 0), 0);
  };

  const getRemainingWeightage = () => {
    return 100 - calculateTotalWeightage();
  };

  const validateQuestions = () => {
    for (const question of questions) {
      if (!question.question_text.trim()) {
        return "All questions must have text";
      }
      if (question.weightage <= 0) {
        return "All questions must have a weightage greater than 0";
      }
      for (let i = 1; i <= 5; i++) {
        if (!question.options[i.toString() as "1" | "2" | "3" | "4" | "5"].trim()) {
          return `All rating options (1-5) must be filled for each question`;
        }
      }
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim() || !formData.description.trim()) {
      toast.error("Please fill in all required template fields");
      return;
    }

    const validationError = validateQuestions();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const totalWeightage = calculateTotalWeightage();
    if (totalWeightage !== 100) {
      toast.error(`Total weightage must be exactly 100%. Current: ${totalWeightage}%`);
      return;
    }

    setIsSubmitting(true);

    try {
      const templateResponse = await createTemplateAPI(formData);
      const templateId = templateResponse.data.id;

      for (const question of questions) {
        const questionData: CreateQuestionData = {
          template_id: templateId,
          question_text: question.question_text,
          weightage: question.weightage,
          options: question.options,
        };
        await createTemplateQuestionAPI(questionData);
      }

      toast.success("Template and questions created successfully!");
      navigate("/admin-appraisals");
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error ||
        error.response?.data?.message ||
        "Failed to create template";
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalWeightage = calculateTotalWeightage();
  const remainingWeightage = getRemainingWeightage();

  return (
    <div className="mt-5">
      <Card extra="p-6">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-navy-700 dark:text-white">
            Create New Template
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Fill in the details below. Default rating descriptions are provided but can be edited.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* ... (Template Info Section remains same) ... */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-navy-700 dark:text-white border-b pb-2">
              Template Information
            </h3>

            <InputField
              id="name"
              name="name"
              label="Template Name *"
              type="text"
              placeholder="Enter template name"
              extra="w-full"
              variant="outlined"
              value={formData.name}
              onChange={handleChange}
              required
            />

            <TextField
              id="description"
              label="Description *"
              placeholder="Enter template description"
              extra="w-full"
              rows={4}
              value={formData.description}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  description: e.target.value,
                });
              }}
            />

            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_active"
                name="is_active"
                checked={formData.is_active}
                onChange={handleChange}
                className="h-4 w-4 text-brand-500 focus:ring-brand-500 border-gray-300 rounded"
              />
              <label
                htmlFor="is_active"
                className="ml-2 text-sm font-medium text-navy-700 dark:text-white"
              >
                Active
              </label>
            </div>
          </div>

          <div className="space-y-4 pt-6 border-t">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <h3 className="text-lg font-semibold text-navy-700 dark:text-white">
                Questions
              </h3>
              <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-end gap-4">
                <div className="text-sm">
                  <span className="text-gray-600 dark:text-gray-400">Total: </span>
                  <span
                    className={`font-semibold ${
                      totalWeightage === 100
                        ? "text-green-600 dark:text-green-400"
                        : totalWeightage > 100
                        ? "text-red-600 dark:text-red-400"
                        : "text-blue-600 dark:text-blue-400"
                    }`}
                  >
                    {totalWeightage}%
                  </span>
                  <span className="text-gray-600 dark:text-gray-400 ml-2">
                    Remaining: {remainingWeightage}%
                  </span>
                </div>
                <Button
                  type="button"
                  varient="secondary"
                  onClick={addQuestion}
                  className="flex items-center gap-2 w-full sm:w-auto"
                >
                  <MdAdd className="h-4 w-4" />
                  Add Question
                </Button>
              </div>
            </div>

            {questions.map((question, index) => (
              <div
                key={index}
                className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg space-y-4 bg-gray-50 dark:bg-navy-700/30"
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-md font-semibold text-navy-700 dark:text-white">
                    Question # {index + 1}
                  </h4>
                  {questions.length > 1 && (
                    <Button
                      type="button"
                      varient="danger"
                      onClick={() => removeQuestion(index)}
                      className="flex items-center gap-1 px-3 py-1"
                    >
                      <MdDelete className="h-4 w-4" />
                      Remove
                    </Button>
                  )}
                </div>

                <TextField
                  id={`question_text_${index}`}
                  label="Question Text *"
                  placeholder="Enter the question"
                  extra="w-full [&_textarea]:bg-white dark:[&_textarea]:bg-navy-800"
                  rows={2}
                  value={question.question_text}
                  onChange={(e) =>
                    handleQuestionChange(index, "question_text", e.target.value)
                  }
                />

                <Slider
                  id={`weightage_${index}`}
                  label="Weightage (%) *"
                  value={question.weightage || 0}
                  min={0}
                  max={100}
                  step={5}
                  onChange={(value) => {
                    handleQuestionChange(index, "weightage", value);
                  }}
                  extra="w-full"
                  showError={question.weightage > remainingWeightage + question.weightage}
                />

                <div className="space-y-2">
                  <label className="text-sm font-bold text-navy-700 dark:text-white">
                    Rating Criteria (Define the meaning of each score) *
                  </label>
                  <p className="text-xs text-gray-500 mb-2">
                    Default values are provided, but you can change them to fit this specific question.
                  </p>
                  
                  {/* 4. Mapped options with Clear Labels */}
                  {(["1", "2", "3", "4", "5"] as const).map((key) => (
                    <InputField
                      key={key}
                      id={`option_${index}_${key}`}
                      // Shows "Score 1 (Lowest Performance)" etc.
                      label={SCORE_LABELS[key]} 
                      type="text"
                      placeholder={`Enter text for score ${key}`}
                      extra="w-full [&_input]:bg-white dark:[&_input]:bg-navy-800"
                      variant="outlined"
                      value={question.options[key]}
                      onChange={(e) =>
                        handleOptionChange(index, key, e.target.value)
                      }
                      required
                    />
                  ))}
                </div>

              </div>
            ))}

            {questions.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <p>No questions added yet. Click "Add Question" to get started.</p>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              varient="secondary"
              onClick={() => navigate("/admin-appraisals")}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" varient="primary" disabled={isSubmitting}>
              {isSubmitting ? "Creating..." : "Create Template"}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default CreateTemplate;